// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only.

// D-SHIP-23 — TanStack Query hooks for shipping (E-02: components never call api.* directly).

import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  createCarrierAccount, getCarrierServices, getPickupLocations, listCarrierAccounts, testCarrierAccount,
  updateCarrierAccount, type CarrierAccountInput, type ServiceOptionsInput,
} from '../../api/shipping'

export const shippingKeys = {
  services: (carrierId: string, input: ServiceOptionsInput, pharmacyId?: string) => ['shipping', 'carrier-services', carrierId, input, pharmacyId ?? 'own'] as const,
  pickupLocations: (carrierId: string, pharmacyId?: string) => ['shipping', 'pickup-locations', carrierId, pharmacyId ?? 'own'] as const,
  accounts: (pharmacyId?: string) => ['shipping', 'carrier-accounts', pharmacyId ?? 'own'] as const,
}

export function useCarrierServices(carrierId: string | undefined, input: ServiceOptionsInput, pharmacyId?: string) {
  return useQuery({
    queryKey: shippingKeys.services(carrierId ?? '', input, pharmacyId),
    queryFn: () => getCarrierServices(carrierId ?? '', input, pharmacyId),
    enabled: !!carrierId,
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  })
}

export function usePickupLocations(carrierId: string | undefined, pharmacyId?: string) {
  return useQuery({
    queryKey: shippingKeys.pickupLocations(carrierId ?? '', pharmacyId),
    queryFn: () => getPickupLocations(carrierId ?? '', pharmacyId),
    enabled: !!carrierId,
    staleTime: 5 * 60_000,
  })
}

export function useCarrierAccounts(pharmacyId?: string) {
  return useQuery({ queryKey: shippingKeys.accounts(pharmacyId), queryFn: () => listCarrierAccounts(pharmacyId) })
}

function useInvalidateCarriers(pharmacyId?: string) {
  const qc = useQueryClient()
  return () => {
    void qc.invalidateQueries({ queryKey: shippingKeys.accounts(pharmacyId) })
    void qc.invalidateQueries({ queryKey: ['shipping', 'pickup-locations'] })
    void qc.invalidateQueries({ queryKey: ['shipping', 'carrier-services'] })
  }
}

export function useCreateCarrierAccount(pharmacyId?: string) {
  const invalidate = useInvalidateCarriers(pharmacyId)
  return useMutation({ mutationFn: (input: CarrierAccountInput) => createCarrierAccount(input, pharmacyId), onSuccess: invalidate })
}

export function useUpdateCarrierAccount(pharmacyId?: string) {
  const invalidate = useInvalidateCarriers(pharmacyId)
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: CarrierAccountInput }) => updateCarrierAccount(id, input, pharmacyId),
    onSuccess: invalidate,
  })
}

export function useTestCarrierAccount(pharmacyId?: string) {
  const invalidate = useInvalidateCarriers(pharmacyId)
  return useMutation({ mutationFn: (id: string) => testCarrierAccount(id, pharmacyId), onSuccess: invalidate })
}
